import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useAccessibility } from '../contexts/AccessibilityContext';
import { isAnyModalOpen } from './useModalDialog';

const isTypingTarget = (el) => {
  if (!el) return false;
  const tag = el.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable;
};

/**
 * Global "Alt+0" shortcut that signs the user out from any page — extends
 * useGlobalNavShortcuts' Alt+1 through Alt+9 (jump to the Nth nav item)
 * with a matching Alt+0 for the one nav-bar action that isn't "go
 * somewhere": signing out. Removing the standalone "Account" nav item in
 * favour of this (plus a Sign Out button in the Settings panel) means
 * switch-scanning software like Mind Express 5 can trigger it directly
 * from wherever the user already is, without first navigating to a
 * dedicated Account page.
 */
export function useSignOutShortcut() {
  const { user, signOut } = useAuth();
  const { announce } = useAccessibility();
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) return undefined;

    const handleKeyDown = (event) => {
      if (!event.altKey) return;
      if (event.key !== '0') return;
      if (isAnyModalOpen()) return;
      if (isTypingTarget(document.activeElement)) return;

      event.preventDefault();
      signOut().then(() => {
        announce('Signed out');
        navigate('/login', { replace: true });
      });
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [user, signOut, announce, navigate]);
}
